import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
};

export const recipientsAPI = {
  getAll: () => api.get('/recipients'),
  create: (data) => api.post('/recipients', data),
  update: (id, data) => api.put(`/recipients/${id}`, data),
  delete: (id) => api.delete(`/recipients/${id}`),
};

export const transactionsAPI = {
  initiate: (data) => api.post('/transactions/initiate', data),
  getAll: () => api.get('/transactions'),
  getById: (id) => api.get(`/transactions/${id}`),
};

export const paymentsAPI = {
  createSession: (data) => api.post('/payments/create-session', data),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
};

export const exchangeRateAPI = {
  getRate: (from, to) => api.get(`/exchange-rates/${from}/${to}`),
};

export const walletAPI = {
  getBalance: () => api.get('/wallet/balance'),
};

export const usersAPI = {
  getProfile: () => api.get('/users/profile'),
  updateProfile: (data) => api.put('/users/profile', data),
  updateKYC: (data) => api.put('/users/kyc', data),
};

// KYC/Compliance APIs
export const complianceAPI = {
  // KYC Application
  startKYC: (customerType) => api.post(`/compliance/kyc/start?customer_type=${customerType}`),
  getKYC: (kycId) => api.get(`/compliance/kyc/${kycId}`),
  getUserKYC: (userId) => api.get(`/compliance/kyc/user/${userId}`),
  updateIndividual: (kycId, data) => api.put(`/compliance/kyc/${kycId}/individual`, data),
  updateCorporate: (kycId, data) => api.put(`/compliance/kyc/${kycId}/corporate`, data),
  addUBO: (kycId, data) => api.post(`/compliance/kyc/${kycId}/ubo`, data),
  addOwnershipLayer: (kycId, data) => api.post(`/compliance/kyc/${kycId}/ownership-layer`, data),
  addDirector: (kycId, data) => api.post(`/compliance/kyc/${kycId}/director`, data),
  addPEP: (kycId, data) => api.post(`/compliance/kyc/${kycId}/pep`, data),
  submitKYC: (kycId) => api.post(`/compliance/kyc/${kycId}/submit`),
  uploadDocument: (kycId, formData) => api.post(`/compliance/kyc/${kycId}/document`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  
  // Admin KYC Management
  getPendingKYC: (params) => api.get('/compliance/admin/kyc/pending', { params }),
  getAllKYC: (params) => api.get('/compliance/admin/kyc/all', { params }),
  firstReview: (kycId, data) => api.put(`/compliance/admin/kyc/${kycId}/first-review`, null, { params: data }),
  secondReview: (kycId, data) => api.put(`/compliance/admin/kyc/${kycId}/second-review`, null, { params: data }),
  blockCustomer: (kycId, reason) => api.put(`/compliance/admin/kyc/${kycId}/block?reason=${encodeURIComponent(reason)}`),
  unblockCustomer: (kycId, notes) => api.put(`/compliance/admin/kyc/${kycId}/unblock?notes=${encodeURIComponent(notes)}`),
  verifyDocument: (kycId, docId, verified, notes) => api.put(`/compliance/admin/kyc/${kycId}/document/${docId}/verify`, null, { params: { verified, notes } }),
  getKYCStats: () => api.get('/compliance/admin/stats/kyc'),
  
  // Admin Team
  getTeam: () => api.get('/compliance/admin/team'),
  addTeamMember: (data) => api.post('/compliance/admin/team', data),
  updateTeamMember: (memberId, data) => api.put(`/compliance/admin/team/${memberId}`, data),
  removeTeamMember: (memberId) => api.delete(`/compliance/admin/team/${memberId}`),
  getRoles: () => api.get('/compliance/admin/roles'),
  
  // Watchlist
  getWatchlist: (params) => api.get('/compliance/admin/watchlist', { params }),
  addWatchlistEntry: (data) => api.post('/compliance/admin/watchlist', data),
  deactivateWatchlistEntry: (entryId) => api.delete(`/compliance/admin/watchlist/${entryId}`),
  screenEntity: (name, country) => api.post(`/compliance/admin/screen?name=${encodeURIComponent(name)}${country ? `&country=${country}` : ''}`),
  
  // Audit Logs
  getAuditLogs: (params) => api.get('/compliance/admin/audit-logs', { params }),

  // Admin KYC Email Actions
  sendInternalKyc: (userId) => api.post(`/admin/users/${userId}/send-internal-kyc`),
  sendBridgeKyc: (userId) => api.post(`/admin/users/${userId}/send-bridge-kyc`),

  // KYC Document Actions
  sendKycDocLink: (userId) => api.post(`/admin/kyc-doc/send-link/${userId}`),
  getKycDocuments: () => api.get('/admin/kyc-documents'),
  getUserKycDocuments: (userId) => api.get(`/admin/kyc-documents/${userId}`),
  reviewKycDocuments: (userId, decision, notes) => api.post(`/admin/kyc-documents/${userId}/review`, { decision, notes }),
  reanalyzeKycDocuments: (userId) => api.post(`/admin/kyc-documents/${userId}/reanalyze`),
  getKycDocFile: (userId, filename) => `${BACKEND_URL}/api/admin/kyc-documents/${userId}/file/${filename}`,

  // USDMint Settlement Actions
  reconcileSettlements: () => api.post('/admin/reconcile-settlements'),
  syncBridgeTransfers: () => api.post('/admin/sync-bridge-transfers'),
  markSettled: (transferId) => api.post(`/admin/mark-settled/${transferId}`),
  settleTransfer: (transferId) => api.post(`/usdmint/settle/${transferId}`),
  settleAllPending: () => api.post('/usdmint/settle-all-pending'),
  getUsdmintBalance: () => api.get('/usdmint/balance'),
};

// Bridge.xyz Crypto APIs
export const bridgeAPI = {
  getStatus: () => api.get('/bridge/status'),
  getSupported: () => api.get('/bridge/supported'),
  createCustomer: () => api.post('/bridge/customer'),
  createTransfer: (data) => api.post('/bridge/transfer', data),
  getTransfer: (id) => api.get(`/bridge/transfer/${id}`),
  getTransfers: () => api.get('/bridge/transfers'),
  // USD → USDC Payment Route
  getPaymentRoute: () => api.get('/bridge/payment-route'),
  createPaymentRouteTransfer: (data) => api.post('/bridge/payment-route/transfer', data),
  getPaymentRouteTransfers: () => api.get('/bridge/payment-route/transfers'),
  // KYC Status
  getKycStatus: () => api.get('/bridge/kyc-status'),
};